import { memo } from 'react'
import { Paperclip } from 'lucide-react'
import { twMerge } from 'tailwind-merge'
import { Button } from '@highlight/ui'
import type { QueuedMessage } from '../../../../store'
import { XIcon } from '../../../icons'

type QueuedBubbleProps = {
  item: QueuedMessage
  onCancel: (item: QueuedMessage) => void | Promise<void>
  isActive: boolean
}

// Single wrapper for both states so React never remounts the bubble when
// isActive flips — keeps the bubble box stable and prevents the 2px layout
// shift between "border border-dashed" and "no border" trees. The dashed
// stroke and rotating gradient ring (.queue-bubble-active-ring in
// styles/index.css) are absolute overlays at -inset-px so both sit at the
// parent's border-box edge regardless of which one is shown.
export const QueuedBubble = memo(({ item, onCancel, isActive }: QueuedBubbleProps) => {
  const canCancel = item.messageId !== null && !item.cancelling
  const attachmentCount = item.attachedContext.length
  const ariaLabel =
    attachmentCount > 0
      ? `Queued message: ${item.prompt} (${attachmentCount} attached)`
      : `Queued message: ${item.prompt}`

  return (
    <div
      role="listitem"
      aria-label={ariaLabel}
      className={twMerge(
        'relative flex max-w-[340px] items-start gap-2 rounded-[12px] border border-transparent px-3 py-2 transition-[color,opacity] duration-300',
        isActive ? 'text-foreground opacity-100' : 'text-foreground-secondary opacity-70',
      )}
    >
      <span
        aria-hidden
        className={twMerge(
          'border-strong pointer-events-none absolute -inset-px rounded-[12px] border border-dashed transition-opacity duration-300',
          isActive ? 'opacity-0' : 'opacity-100',
        )}
      />
      <span
        aria-hidden
        className={twMerge(
          'queue-bubble-active-ring pointer-events-none absolute -inset-px rounded-[12px] transition-opacity duration-300',
          isActive ? 'opacity-100' : 'opacity-0',
        )}
      />
      {attachmentCount > 0 && (
        <span
          aria-hidden
          className="text-foreground-secondary relative flex shrink-0 items-center gap-0.5 self-center text-[11px] font-medium leading-none"
        >
          <Paperclip size={12} strokeWidth={2} />
          {attachmentCount > 1 ? attachmentCount : null}
        </span>
      )}
      <span className="type-prose-md relative whitespace-pre-wrap break-words">{item.prompt}</span>
      <Button
        size="xs"
        shape="square"
        variant="ghost"
        onClick={() => void onCancel(item)}
        disabled={!canCancel}
        aria-label="Cancel queued message"
        className="text-foreground-secondary hover:text-foreground relative -mr-1 size-5 shrink-0 rounded"
      >
        <XIcon size={14} />
      </Button>
    </div>
  )
})

QueuedBubble.displayName = 'QueuedBubble'
